import { useState } from 'react';
import { formatDateTime } from '../../../../../utils/dates';
import { AiGlyph } from '../../../../monitoring/messages/triage/AiGlyph';
import type { DmComposerHandle } from '../composer/DmMessageComposer';
import { shouldConfirmReplace } from '../composer/replaceComposerText';
import { AiDraftActions, AiDraftReplaceConfirm } from './AiDraftActions';
import { AiDraftNewMessageHint } from './AiDraftNewMessageHint';
import type { AiDraftState } from './aiThreadDraft';

interface AiDraftBubbleProps {
    draft: AiDraftState;
    generatedAt: string | null;
    composer: DmComposerHandle;
}

// Dashed and muted so it never reads as a sent message; it is not part of the message list.
export function AiDraftBubble({ draft, generatedAt, composer }: AiDraftBubbleProps) {
    const [confirmingReplace, setConfirmingReplace] = useState(false);
    // Swapping the buttons would drop focus to the page, so Cancel hands it back to Use draft.
    const [refocusUse, setRefocusUse] = useState(false);
    const generated = formatDateTime(generatedAt);

    const fillComposer = () => {
        setConfirmingReplace(false);
        setRefocusUse(false);
        composer.replaceText(draft.reply);
    };

    const cancelReplace = () => {
        setConfirmingReplace(false);
        setRefocusUse(true);
    };

    const handleUse = () => {
        if (shouldConfirmReplace(composer.readText(), draft.reply)) {
            setConfirmingReplace(true);
            return;
        }
        fillComposer();
    };

    return (
        <section aria-label="AI draft reply" className="flex flex-col items-end gap-1">
            <div className="max-w-[75%] rounded-2xl rounded-br-sm border border-dashed border-violet-300 bg-violet-50 px-3.5 py-2.5 text-sm leading-snug text-gray-800">
                <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                    <AiGlyph className="text-xs" />
                    AI draft
                </div>
                <p className="whitespace-pre-wrap break-words">{draft.reply}</p>
                {draft.newSinceDraft && <AiDraftNewMessageHint />}
                <div className="mt-2.5 border-t border-dashed border-violet-200 pt-2">
                    {confirmingReplace ? (
                        <AiDraftReplaceConfirm onCancel={cancelReplace} onReplace={fillComposer} />
                    ) : (
                        <AiDraftActions
                            basedOn={draft.basedOn}
                            onUse={handleUse}
                            focusOnMount={refocusUse}
                        />
                    )}
                </div>
            </div>
            <span className="text-[10px] text-gray-500">
                Not sent{generated && ` · generated ${generated}`}
            </span>
        </section>
    );
}
